import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';

import {
    useDeleteVehiceMutation,
    useEditUserVehicleMutation,
    useGetVehicleByIdQuery,
} from '../../Features/userVehicles';

import {
    ArrowLeftIcon,
    TruckIcon,
    TrashIcon,
    SaveIcon,
    XIcon,
} from '@heroicons/react/outline';

function EditVehicleView() {
    const navigate = useNavigate();
    const { vehicle_id } = useParams();
    const { isOnline } = useIsOnline();

    const [progress, setProgress] = useState(0);

    const [errors, setErrors] = useState({
        name: '',
    });

    const [input, setInput] = useState({
        name: '',
    });

    const [editVehicle, { isLoading: isLoadingEditVehicle }] =
        useEditUserVehicleMutation();

    const [deleteVehicle, { isLoading: isLoadingDeleteVehicle }] =
        useDeleteVehiceMutation();

    const {
        data: vehicle,
        isLoading,
        isSuccess,
    } = useGetVehicleByIdQuery(vehicle_id);

    useEffect(() => {
        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess) {
            setProgress(100);

            if (vehicle?.hasOwnProperty('data')) {
                setInput(vehicle.data);
            }
        }
    }, [vehicle, isLoading, isSuccess]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleDelete = async (e, id) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again'
            );
        }

        if (!id) {
            return;
        }

        if (
            window.confirm(
                'Are you sure you want to delete this vehicle? This action cannot be undone.'
            )
        ) {
            try {
                setProgress(80);

                const resp = await deleteVehicle(id).unwrap();

                if (resp?.hasOwnProperty('success')) {
                    setProgress(100);
                    return navigate(-1);
                }
            } catch (err) {
                setProgress(100);

                swal(
                    'Error',
                    err?.data?.message ||
                        err?.data?.error ||
                        err?.message ||
                        'Something went wrong while deleting the vehicle.',
                    'error'
                );
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again'
            );
        }

        setErrors({
            name: '',
        });

        try {
            setProgress(80);

            const resp = await editVehicle(input).unwrap();

            if (resp?.hasOwnProperty('success')) {
                setProgress(100);
                navigate(-1);
            }
        } catch (err) {
            setProgress(100);

            if (err?.data?.hasOwnProperty('errors')) {
                setErrors({
                    name: err.data.errors?.name || '',
                });
            } else if (err?.data?.hasOwnProperty('error')) {
                setInput((prev) => ({
                    ...prev,
                    errorMassage: err.data.error,
                }));
            } else {
                swal(
                    'Error',
                    err?.data?.message ||
                        err?.message ||
                        'Something went wrong while updating the vehicle.',
                    'error'
                );
            }
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again'
            );
        }

        navigate(-1);
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            {/* Header */}
            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <TruckIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                                Edit Vehicle
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Update vehicle information and manage this
                                vehicle
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={back}
                        disabled={
                            isLoadingEditVehicle ||
                            isLoadingDeleteVehicle
                        }
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-[#80b643] hover:text-[#80b643] transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Back
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="w-full px-4 sm:px-6 lg:px-8 mt-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} autoComplete="off">

                        {/* Vehicle Information */}
                        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                            {/* Card Header */}
                            <div className="px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                        <TruckIcon className="w-5 h-5 text-[#80b643]" />
                                    </div>

                                    <div>
                                        <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
                                            Vehicle Information
                                        </h2>

                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Update the vehicle name below
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Form */}
                            <div className="p-5 sm:p-6">
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                    >
                                        Vehicle Name
                                    </label>

                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={input.name || ''}
                                        onChange={handleChange}
                                        placeholder="Enter vehicle name or registration number"
                                        className={`w-full px-4 py-3 rounded-lg border ${
                                            errors.name
                                                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                                : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]/20'
                                        } bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white placeholder-gray-400 outline-none focus:ring-2 transition`}
                                    />

                                    {errors.name && (
                                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Delete Vehicle */}
                        <div className="mt-5 bg-white dark:bg-gray-900 border border-red-100 dark:border-red-900/40 rounded-xl shadow-sm overflow-hidden">
                            <div className="p-5 sm:p-6">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                                            <TrashIcon className="w-5 h-5 text-red-500" />
                                        </div>

                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                                                Delete Vehicle
                                            </h3>

                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                Permanently delete this vehicle
                                                from your fleet.
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={(e) =>
                                            handleDelete(e, input.id)
                                        }
                                        disabled={
                                            isLoadingDeleteVehicle ||
                                            isLoadingEditVehicle
                                        }
                                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <TrashIcon className="w-4 h-4" />

                                        {isLoadingDeleteVehicle
                                            ? 'Deleting...'
                                            : 'Delete Vehicle'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4 sm:p-5">
                            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">

                                <button
                                    type="button"
                                    onClick={back}
                                    disabled={
                                        isLoadingEditVehicle ||
                                        isLoadingDeleteVehicle
                                    }
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <XIcon className="w-4 h-4" />
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={
                                        isLoadingEditVehicle ||
                                        isLoadingDeleteVehicle
                                    }
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium border border-[#80b643] shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <SaveIcon className="w-4 h-4" />

                                    {isLoadingEditVehicle
                                        ? 'Saving...'
                                        : 'Save Changes'}
                                </button>

                            </div>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditVehicleView;